import { create } from "zustand";
import { IProgressReport } from "@/types/report";
import {
  clearStoredAwaitedReport,
  readAwaitedReport,
  writeAwaitedReport,
} from "@/services/awaitedReportStorage";

interface ReportsStore {
  generatingReports: Record<number, IProgressReport>;
  setGeneratingReports: (reports: Record<number, IProgressReport>) => void;
  clearGeneratingReports: () => void;

  generateDialogRequested: boolean;
  requestedGenerateReportId: number | null;
  setGenerateDialogRequest: (reportId: number) => void;
  /**
   * Open the progress dialog WITHOUT claiming the report.
   *
   * Claiming (`setGenerateDialogRequest`) is for the tab that started the run:
   * it is what routes the user to the finished report. Merely looking at a
   * run's progress — the dashboard auto-opening the dialog because this
   * company has a build in flight, the mobile bar, "Show progress" — must not
   * route anyone: a workspace member watching a colleague's run dismisses the
   * dialog or gets the completion toast, never yanked.
   */
  requestProgressView: (reportId: number) => void;
  clearGenerateDialogRequest: () => void;

  /**
   * The report the user is waiting on, and expects to land on when it finishes.
   *
   * Deliberately separate from the dialog-request flags above, which the dialog
   * clears the moment it opens. "Take me to my report when it's done" has to
   * outlive the dialog: its own copy invites closing it ("You can safely close
   * this window"), and the user may be in another window entirely when the
   * report lands. It also has to outlive the *selected period* — someone who
   * kicks off a run while reading an older month still means "show me the new
   * one", which is the only reason the dashboard is allowed to move them off a
   * historical report they chose.
   */
  awaitedReportId: number | null;
  /**
   * Is the progress dialog for the awaited report on screen? Completion only
   * navigates when the user is passively waiting — watching this dialog, or
   * sitting on the company's dashboard — never out of unrelated work
   * (`useAwaitedReport`).
   */
  awaitedDialogOpen: boolean;
  /**
   * When the awaited report was claimed. Paces the completion experience: a
   * "run" that resolves near-instantly (the backend deduplicates to the
   * month's existing report) still gets MIN_GENERATION_DWELL_MS of visible
   * progress before the handover, or it reads as fake.
   */
  awaitedClaimedAt: number | null;
  clearAwaitedReport: () => void;
  /** Remember that the user put the progress dialog away, without forgetting the report. */
  setAwaitedDialogOpen: (open: boolean) => void;

  // Active company while inside the Virtual Buyer section, so the sidebar
  // company dropdown can highlight it on /virtual-buyer routes (the reports
  // flow derives its active company from the /reports/:id URL; VB routes
  // don't carry the company id, so VbLayout publishes it here instead).
  activeVbCompanyId: number | null;
  setActiveVbCompanyId: (companyId: number | null) => void;
}

// Restored from the tab, so a refresh mid-generation keeps both the progress
// dialog (if it was up) and the promise to land the user on the finished report.
const restored = readAwaitedReport();

export const useReportsStore = create<ReportsStore>((set, get) => ({
  generatingReports: {},
  
  setGeneratingReports: (reports: Record<number, IProgressReport>) =>
    set({ generatingReports: reports }),
    
  clearGeneratingReports: () =>
    set({ generatingReports: {} }),

  // Re-arms the dialog after a reload only if it was actually on screen, so a
  // dismissal the user meant isn't undone by refreshing.
  generateDialogRequested: restored?.dialogOpen ?? false,
  requestedGenerateReportId: restored?.dialogOpen ? restored.reportId : null,

  // Every "the user started / is watching this report" path funnels through
  // here, so this is also where the awaited report is claimed.
  setGenerateDialogRequest: (reportId: number) => {
    const claimedAt = Date.now();
    writeAwaitedReport(reportId, true, claimedAt);
    set({
      generateDialogRequested: true,
      requestedGenerateReportId: reportId,
      awaitedReportId: reportId,
      awaitedDialogOpen: true,
      awaitedClaimedAt: claimedAt,
    });
  },

  requestProgressView: (reportId: number) =>
    set({
      generateDialogRequested: true,
      requestedGenerateReportId: reportId,
    }),

  // Only the dialog-request flags — NOT `awaitedReportId`. The dialog calls this
  // as soon as it opens, and clearing the awaited report here would put us back
  // to "navigation only happens while the dialog is on screen".
  clearGenerateDialogRequest: () =>
    set({
      generateDialogRequested: false,
      requestedGenerateReportId: null
    }),

  awaitedReportId: restored?.reportId ?? null,
  awaitedDialogOpen: restored?.dialogOpen ?? false,
  awaitedClaimedAt: restored?.at ?? null,

  clearAwaitedReport: () => {
    clearStoredAwaitedReport();
    set({ awaitedReportId: null, awaitedDialogOpen: false, awaitedClaimedAt: null });
  },

  setAwaitedDialogOpen: (open: boolean) => {
    const { awaitedReportId: id, awaitedClaimedAt } = get();
    if (id == null) return;
    writeAwaitedReport(id, open, awaitedClaimedAt ?? Date.now());
    set({ awaitedDialogOpen: open });
  },

  activeVbCompanyId: null,
  setActiveVbCompanyId: (companyId: number | null) =>
    set({ activeVbCompanyId: companyId }),
}));
